import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import NoData from '../../../components/common/NoData';
import { getNotifications } from '../../../services/user/notification.services';
import { getPointsActivity } from '../../../services/user/dashboard.services';

const getNotificationPayload = (notification) => {
  if (!notification?.content_json) return {};
  if (typeof notification.content_json === 'object') return notification.content_json;

  try {
    return JSON.parse(notification.content_json);
  } catch (_error) {
    return {};
  }
};

const isPointsNotification = (notification) => {
  const payload = getNotificationPayload(notification);
  const eventType = notification?.event || payload?.data?.eventType || '';
  const points = payload?.data?.points;

  return (
    eventType === 'GAMIFICATION_POINTS_EARNED' ||
    notification?.is_earning_point === 1 ||
    notification?.is_earning_point === '1' ||
    notification?.isEarningPoint === true ||
    points !== undefined
  );
};

const normalizePointActivity = (activity) => ({
  id: `activity-${activity.id}`,
  title: activity.title || 'Points earned',
  body: activity.body || `You earned ${activity.earning_points || 0} points from ${activity.source_label || 'activity'}.`,
  createdAt: activity.createdAt || activity.created_at,
  source_label: activity.source_label,
  earning_points: activity.earning_points,
});

function PointsNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    fetchPointsNotifications();
  }, [page]);

  const fetchPointsNotifications = async () => {
    try {
      setLoading(true);
      const [activityResponse, notificationResponse] = await Promise.allSettled([
        getPointsActivity(50),
        getNotifications({
        page,
        limit: 50,
        search: ''
        })
      ]);

      const activityRows =
        activityResponse.status === 'fulfilled' && activityResponse.value?.success && Array.isArray(activityResponse.value?.data)
          ? activityResponse.value.data.map(normalizePointActivity)
          : [];

      const response = notificationResponse.status === 'fulfilled' ? notificationResponse.value : null;

      if (response?.success && response.notifications) {
        const pointsNotifications = response.notifications.filter(isPointsNotification);
        const mergedPoints = page === 1 && activityRows.length > 0 ? activityRows : pointsNotifications;

        if (page === 1) {
          setNotifications(mergedPoints);
        } else {
          setNotifications(prev => [...prev, ...pointsNotifications]);
        }

        // Check if there are more pages
        setHasMore(response.pagination && response.pagination.currentPage < response.pagination.totalPages);
      } else if (page === 1 && activityRows.length > 0) {
        setNotifications(activityRows);
        setHasMore(false);
      }
    } catch (error) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      setPage(prev => prev + 1);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    }
  };

  const getNotificationIcon = (notification) => {
    const source = notification.source_label || notification.body || notification.title || '';
    if (source.includes('Assignment')) return 'fluent:document-24-regular';
    if (source.includes('discussion') || source.includes('Post')) return 'fluent:chat-24-regular';
    if (source.includes('Course')) return 'fluent:book-24-regular';
    if (source.includes('Badge')) return 'fluent:trophy-24-regular';
    if (source.includes('Streak')) return 'fluent:fire-24-regular';
    if (source.includes('Live class')) return 'fluent:video-24-regular';
    return 'fluent:star-24-regular';
  };

  const getNotificationColor = (notification) => {
    const source = notification.source_label || notification.body || notification.title || '';
    if (source.includes('Assignment')) return 'tw-text-brand-600';
    if (source.includes('discussion') || source.includes('Post')) return 'tw-text-emerald-600';
    if (source.includes('Course')) return 'tw-text-sky-600';
    if (source.includes('Badge')) return 'tw-text-amber-600';
    if (source.includes('Streak') || source.includes('Live class')) return 'tw-text-rose-600';
    return 'tw-text-slate-500';
  };

  return (
        <div className="tw-min-w-0 tw-w-full">
          <div className="ui-card tw-flex tw-h-[25rem] tw-min-w-0 tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-shadow-sm">
            <div className="ui-card-header tw-flex tw-min-h-12 tw-shrink-0 tw-items-center tw-border-b tw-border-slate-100 tw-bg-white tw-px-4 tw-py-3">
              <h5 className="tw-m-0 tw-flex tw-min-w-0 tw-items-center tw-text-sm tw-font-semibold tw-text-slate-900">
                <Icon icon="fluent:star-24-regular" className="tw-me-2 tw-shrink-0 tw-text-brand-600" width={20} height={20} />
                <span className="tw-truncate">Points & Rewards Notifications</span>
              </h5>
            </div>
            <div className="ui-card-body tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-p-3">
              {loading && page === 1 ? (
                <div className="tw-text-center tw-py-6">
                  <div className="!tw-text-[var(--primary-color)] tw-w-[1.5em] tw-h-[1.5em] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] ui-spinner tw-text-brand-600" role="status">
                    <span className="tw-sr-only">Loading...</span>
                  </div>
                  <p className="!tw-text-[var(--secondary-color)] tw-mt-2 tw-text-slate-500">Loading your points notifications...</p>
                </div>
              ) : notifications.length === 0 ? (
                <NoData
                  message="No Points Notifications"
                  description="You haven't earned any points yet. Start completing assignments and activities to see your rewards here!"
                  icon="fluent:star-24-regular"
                />
              ) : (
                <div
                  className="ui-list-group tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-2 tw-overflow-y-auto [overscroll-behavior:contain] max-[767px]:[overscroll-behavior:auto]"
                >
                  {notifications.map((notification, index) => (
                    <div key={notification.id} className="ui-list-group-item tw-rounded-lg tw-border tw-border-slate-200 tw-p-3">
                      <div className="tw-flex tw-items-start tw-gap-2">
                        <div className="tw-shrink-0">
                          <div className={`tw-rounded-full tw-bg-slate-50 tw-flex tw-items-center tw-justify-center   ${getNotificationColor(notification)}`}
                               style={{ width: '36px', height: '36px' }}>
                            <Icon icon={getNotificationIcon(notification)} width="18" height="18" />
                          </div>
                        </div>

                        <div className="tw-grow min-w-0">
                          <div className="tw-flex tw-flex-col sm:tw-flex-row tw-justify-between tw-items-start tw-gap-1 tw-mb-1">
                            <h6 className="tw-mb-0 tw-font-bold tw-break-words" style={{ fontSize: 'clamp(0.8rem, 1.5vw, 0.9rem)' }}>
                              {notification.title}
                            </h6>
                            <small className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-shrink-0">
                              {formatDate(notification.createdAt)}
                            </small>
                          </div>
                          <p className="!tw-text-[var(--secondary-color)] tw-mb-0 tw-text-slate-500 tw-break-words" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.85rem)', lineHeight: '1.4' }}>
                              {notification.body}
                            </p>
                          {notification.source_label && (
                            <div className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-mt-1 tw-text-sm tw-text-slate-500 tw-break-words">
                              Source: {notification.source_label}
                            </div>
                          )}

                          {/* Points Badge */}
                          {(notification.title || '').toLowerCase().includes('earned') && (
                            <div className="tw-mt-2">
                              <span className={"[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] !tw-text-[var(--success-color)] [&.tw-bg-brand-50]:!tw-bg-[#e7f1ff] [&.tw-bg-brand-50]:tw-border-[1px] [&.tw-bg-brand-50]:tw-border-solid [&.tw-bg-brand-50]:tw-border-[#b6d4fe] tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-text-[0.75rem] tw-font-medium tw-leading-[1rem] tw-inline-flex tw-items-center tw-whitespace-nowrap tw-rounded-full tw-border tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-leading-4 tw-capitalize tw-bg-emerald-50 tw-border-emerald-600 tw-text-emerald-600"}>
                                <Icon icon="fluent:star-24-regular" width="12" height="12" className="tw-me-1" />
                                {notification.earning_points ? `${notification.earning_points} Points Earned` : 'Points Earned'}
                              </span>
                            </div>
                          )}
                        </div>


                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Load More Button */}
              {hasMore && notifications.length > 0 && (
                <div className="tw-text-center tw-py-4 tw-border-t tw-bg-white" style={{ position: 'sticky', bottom: 0 }}>
                  <button
                    className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [[data-theme='dark']_&]:tw-border-[#4895ef] [[data-theme='dark']_&]:tw-text-[#4895ef] [[data-theme='dark']_&:hover]:tw-bg-[#4895ef] [[data-theme='dark']_&:hover]:tw-text-[white] tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] tw-border-[rgb(199_210_254_/_var(--tw-border-opacity,_1))] tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-border-opacity:1] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] [&:hover]:tw-text-[white] [&:hover]:tw-bg-[#4169E1] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] tw-rounded-[0.375rem] tw-pl-3 tw-pr-3 tw-pt-1.5 tw-pb-1.5 tw-text-[0.75rem] tw-leading-[1rem] ui-button ui-button-outline-primary ui-button-sm"}
                    onClick={loadMore}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="tw-text-[#3152e8] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] tw-w-[1em] tw-h-[1em] ui-spinner ui-spinner-sm tw-me-2" role="status"></span>
                        Loading...
                      </>
                    ) : (
                      'Load More Notifications'
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
  );
}

export default PointsNotifications;
